/*
 * Case-scoped invalidation bus that reaches outside the Case IDE tree.
 *
 * `CaseIdeProvider` fans `data-invalidate` parts out to the panes mounted
 * under it. The case header (`CaseHeader` and its chips) mounts above that
 * provider, so it cannot subscribe through context. The provider forwards
 * every invalidation here as well, keyed by the case's individual id, and
 * the header subscribes with `useOnCaseInvalidate`. Mirrors
 * `clientInvalidation.ts` on the client profile.
 */
import { useEffect, useRef } from "react";

import type { CaseInvalidateTarget, InvalidateTarget } from "../types";

export type CaseInvalidateListener = (targets: CaseInvalidateTarget[]) => void;

const listeners = new Map<string, Set<CaseInvalidateListener>>();

const CASE_TARGETS: ReadonlySet<string> = new Set<CaseInvalidateTarget>([
  "fs",
  "drafts",
  "packet",
  "forms",
  "research",
  "case",
  "autocategorize",
  "plans",
  "receipts",
  "key_facts"
]);

/** Keep only the case-scoped targets out of a mixed `data-invalidate` list. */
export function caseTargets(
  targets: readonly InvalidateTarget[]
): CaseInvalidateTarget[] {
  return targets.filter((t): t is CaseInvalidateTarget => CASE_TARGETS.has(t));
}

/** Notify everything mounted for `individualId` that `targets` went stale. */
export function invalidateCase(
  individualId: string,
  targets: readonly InvalidateTarget[]
): void {
  const id = String(individualId ?? "").trim();
  const hit = caseTargets(targets);
  if (!id || hit.length === 0) return;
  const set = listeners.get(id);
  if (!set) return;
  // Copy: a listener may unsubscribe while we iterate.
  for (const fn of Array.from(set)) fn(hit);
}

/** Subscribe to invalidations for one case; returns the unsubscribe. */
export function subscribeCaseInvalidation(
  individualId: string,
  listener: CaseInvalidateListener
): () => void {
  const id = String(individualId ?? "").trim();
  if (!id) return () => undefined;
  let set = listeners.get(id);
  if (!set) {
    set = new Set();
    listeners.set(id, set);
  }
  set.add(listener);
  return () => {
    const current = listeners.get(id);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(id);
  };
}

/**
 * Run `refetch` when any of `targets` is invalidated for `individualId`. The
 * latest `refetch` is always used, so callers need not memoize it.
 */
export function useOnCaseInvalidate(
  individualId: string | null | undefined,
  targets: readonly CaseInvalidateTarget[],
  refetch: (matched: CaseInvalidateTarget[]) => void
): void {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const key = targets.join("|");
  useEffect(() => {
    if (!individualId) return undefined;
    const wanted = new Set(key.split("|").filter(Boolean));
    return subscribeCaseInvalidation(individualId, (hit) => {
      const matched = hit.filter((t) => wanted.has(t));
      if (matched.length) refetchRef.current(matched);
    });
  }, [individualId, key]);
}

/** Test hook: number of live subscribers for a case. */
export function listenerCountForTests(individualId: string): number {
  return listeners.get(individualId)?.size ?? 0;
}
